// src/pages/Admin/Feedback.js
import React, { useEffect, useState } from 'react';
import adminApi from '../../services/adminApi';
import styles from './Feedback.module.css';

export default function Feedback() {
    const [feedback, setFeedback] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        type: '',
        status: '',
        search: '',
        page: 1
    });
    const [pagination, setPagination] = useState(null);
    const [selectedFeedback, setSelectedFeedback] = useState(null);

    useEffect(() => {
        fetchFeedback();
    }, [filters]);

    const fetchFeedback = async () => {
        try {
            setLoading(true);
            const data = await adminApi.listFeedback(filters);
            setFeedback(data.feedback);
            setPagination(data.pagination);
        } catch (err) {
            console.error('Failed to load feedback:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (id, status) => {
        try {
            await adminApi.updateFeedbackStatus(id, status);
            fetchFeedback();
            if (selectedFeedback?.id === id) {
                setSelectedFeedback(prev => ({ ...prev, status }));
            }
        } catch (err) {
            console.error('Failed to update status:', err);
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'new': return styles.statusNew;
            case 'reviewed': return styles.statusReviewed;
            case 'planned': return styles.statusPlanned;
            case 'resolved': return styles.statusResolved;
            case 'dismissed': return styles.statusDismissed;
            default: return '';
        }
    };

    const getTypeBadgeClass = (type) => {
        switch (type) {
            case 'bug': return styles.typeBug;
            case 'feature_idea': return styles.typeFeature;
            case 'coin_issue': return styles.typeCoin;
            case 'quiz_question_issue': return styles.typeQuiz;
            default: return '';
        }
    };

    return (
        <div className={styles.feedback}>
            <div className={styles.header}>
                <h1 className={styles.pageTitle}>Feedback Submissions</h1>
            </div>

            <div className={styles.filters}>
                <input
                    type="text"
                    placeholder="Search..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                    className={styles.searchInput}
                />

                <select
                    value={filters.type}
                    onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value, page: 1 }))}
                    className={styles.select}
                >
                    <option value="">All Types</option>
                    <option value="bug">Bug</option>
                    <option value="feature_idea">Feature Idea</option>
                    <option value="coin_issue">Coin Issue</option>
                    <option value="quiz_question_issue">Quiz Question Issue</option>
                    <option value="collection_idea">Collection Idea</option>
                    <option value="something_confusing">Something Confusing</option>
                    <option value="general_feedback">General Feedback</option>
                </select>

                <select
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
                    className={styles.select}
                >
                    <option value="">All Statuses</option>
                    <option value="new">New</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="planned">Planned</option>
                    <option value="resolved">Resolved</option>
                    <option value="dismissed">Dismissed</option>
                </select>
            </div>

            {loading ? (
                <div className={styles.loading}>Loading feedback...</div>
            ) : feedback.length === 0 ? (
                <div className={styles.empty}>No feedback found</div>
            ) : (
                <>
                    <div className={styles.table}>
                        <table>
                            <thead>
                            <tr>
                                <th>Type</th>
                                <th>From</th>
                                <th>Message</th>
                                <th>Status</th>
                                <th>Date</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {feedback.map(item => (
                                <tr key={item.id}>
                                    <td>
                                            <span className={`${styles.typeBadge} ${getTypeBadgeClass(item.type)}`}>
                                                {item.type.replace(/_/g, ' ')}
                                            </span>
                                    </td>
                                    <td>
                                        <div className={styles.userInfo}>
                                            <div className={styles.username}>
                                                {item.user?.username || item.username || 'Logged-out user'}
                                            </div>
                                            <div className={styles.email}>
                                                {item.user?.email || item.email || '—'}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className={styles.messagePreview}>
                                            {item.message.substring(0, 100)}
                                            {item.message.length > 100 && '...'}
                                        </div>
                                    </td>
                                    <td>
                                        <select
                                            value={item.status}
                                            onChange={(e) => handleStatusChange(item.id, e.target.value)}
                                            className={`${styles.statusSelect} ${getStatusBadgeClass(item.status)}`}
                                        >
                                            <option value="new">New</option>
                                            <option value="reviewed">Reviewed</option>
                                            <option value="planned">Planned</option>
                                            <option value="resolved">Resolved</option>
                                            <option value="dismissed">Dismissed</option>
                                        </select>
                                    </td>
                                    <td className={styles.date}>
                                        {new Date(item.createdAt).toLocaleDateString()}
                                    </td>
                                    <td>
                                        <button
                                            onClick={() => setSelectedFeedback(item)}
                                            className={styles.viewBtn}
                                        >
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {pagination && pagination.totalPages > 1 && (
                        <div className={styles.pagination}>
                            <button
                                disabled={pagination.page === 1}
                                onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                                className={styles.pageBtn}
                            >
                                Previous
                            </button>
                            <span className={styles.pageInfo}>
                                Page {pagination.page} of {pagination.totalPages}
                            </span>
                            <button
                                disabled={pagination.page === pagination.totalPages}
                                onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                                className={styles.pageBtn}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}

            {selectedFeedback && (
                <FeedbackDetailModal
                    feedback={selectedFeedback}
                    onClose={() => setSelectedFeedback(null)}
                    onStatusChange={handleStatusChange}
                />
            )}
        </div>
    );
}

function FeedbackDetailModal({ feedback, onClose, onStatusChange }) {
    return (
        <div className={styles.modal} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2>Feedback Detail</h2>
                    <button onClick={onClose} className={styles.closeBtn}>×</button>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.detailRow}>
                        <label>Type:</label>
                        <span>{feedback.type.replace(/_/g, ' ')}</span>
                    </div>

                    <div className={styles.detailRow}>
                        <label>From:</label>
                        <span>{feedback.user?.username || feedback.username || 'Logged-out user'}</span>
                    </div>

                    <div className={styles.detailRow}>
                        <label>Email:</label>
                        <span>{feedback.user?.email || feedback.email || '—'}</span>
                    </div>

                    <div className={styles.detailRow}>
                        <label>Message:</label>
                        <p className={styles.messageText}>{feedback.message}</p>
                    </div>

                    <div className={styles.detailRow}>
                        <label>Page URL:</label>
                        <span className={styles.url}>{feedback.page_url || '—'}</span>
                    </div>

                    <div className={styles.detailRow}>
                        <label>Platform:</label>
                        <span>{feedback.platform || '—'}</span>
                    </div>

                    <div className={styles.detailRow}>
                        <label>Submitted:</label>
                        <span>{new Date(feedback.created_at).toLocaleString()}</span>
                    </div>

                    <div className={styles.detailRow}>
                        <label>Status:</label>
                        <select
                            value={feedback.status}
                            onChange={(e) => onStatusChange(feedback.id, e.target.value)}
                            className={styles.statusSelect}
                        >
                            <option value="new">New</option>
                            <option value="reviewed">Reviewed</option>
                            <option value="planned">Planned</option>
                            <option value="resolved">Resolved</option>
                            <option value="dismissed">Dismissed</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
}